import { useRequest } from 'ahooks';
import { useAtomValue, useSetAtom } from 'jotai';
import produce from 'immer';
import { queryItemList, queryItemListByKeyword, queryItemListBySellerSku } from './api';
import {
  useQueryParams,
  CurrentPageExternalItemIdsState,
  ItemIdMap,
  CurrentItems,
  useStoreScope,
} from './state';
import { ItemIdMap as ItemIdMap2, useSetCount } from './stateForSellerSku';
import { ConfigContext, useStoreLimitContext } from './context';
import { useContext } from 'react';
import { getItemsByInternalIds } from '@knowledge/common/cache/spu';
import { filter, forEach, keyBy, set, values } from 'lodash-es';
import { stores2storeScopes } from '@knowledge/Activity/share';
import { cacheItems } from '@knowledge/common/cache/spu';

/**
 * 获取商品列表数据
 */
export function useListData() {
  const params = useQueryParams();
  const {
    storeType,
    storeScopes: defaultStoreScopes,
    internalItemId,
    sideStore,
  } = useStoreLimitContext();
  const [storeScopes] = useStoreScope();
  const setIds = useSetAtom(CurrentPageExternalItemIdsState);
  const setMap = useSetAtom(ItemIdMap);
  const list = useAtomValue(CurrentItems);
  const { excludeSpuIds, skuSelectDisabled } = useContext(ConfigContext);

  const { data, loading } = useRequest(
    () =>
      queryItemList({
        ...params,
        storeType,
        ...(sideStore
          ? {
              platformStoreScope: stores2storeScopes([
                {
                  platform: sideStore.externalType,
                  region: sideStore.region,
                  storeId: sideStore.id,
                },
              ]),
            }
          : (storeScopes?.length || 0) > 0
          ? { platformStoreScope: storeScopes }
          : { platformStoreScope: defaultStoreScopes }),
        internalItemId,
      }),
    {
      refreshDeps: [params, storeScopes, internalItemId, sideStore],
      onSuccess: async (data) => {
        const excludeSpuIdsMap = excludeSpuIds.reduce((acc, cur) => {
          acc[cur] = true;
          return acc;
        }, {} as Record<string, boolean>);
        const { itemList: _itemList } = data;
        cacheItems(_itemList);
        const itemList = _itemList.filter((e) => !excludeSpuIdsMap[e.internalItemId]);
        const ids = itemList.map((item) => item.externalItemId);
        setIds(ids);
        setMap(
          produce((draft) => {
            itemList.forEach((item) => {
              item._checked = false;
              item.skuInfo = item.skuInfo?.map((sku) => {
                return {
                  ...sku,
                  _checked: false,
                };
              });
              if (!draft[item.externalItemId]) {
                draft[item.externalItemId] = item;
              } else if (!skuSelectDisabled && item.skuInfo?.length > 0) {
                const skuInfoMap = keyBy(item.skuInfo, 'skuId');
                forEach(draft[item.externalItemId].skuInfo, (e) => {
                  set(skuInfoMap, `${e.skuId}._checked`, e._checked);
                });
                item.skuInfo = values(skuInfoMap);
                const checkedLength = filter(item.skuInfo, '_checked').length;
                if (checkedLength === 0) {
                  item._checked = false;
                  item._indeterminate = false;
                } else if (checkedLength === item.skuInfo.length) {
                  item._checked = true;
                  item._indeterminate = false;
                } else {
                  item._checked = false;
                  item._indeterminate = true;
                }
                draft[item.externalItemId] = item;
              }
            });
          }),
        );
      },
    },
  );

  return { pageInfo: data?.pageInfo, listData: list, loading } as const;
}

/**
 * 获取sellerSku 相同商品列表
 */
export function useSameSellerSkuListData(sellerSku: string, internalItemId: string) {
  const setMap = useSetAtom(ItemIdMap2);
  const setCount = useSetCount();
  const { loading, runAsync } = useRequest(() => queryItemListBySellerSku(sellerSku), {
    refreshDeps: [sellerSku],
    onSuccess: async (data) => {
      const { itemList } = data;
      const showItemList = itemList.filter((e) => e.internalItemId !== internalItemId);
      setMap(
        produce((draft) => {
          showItemList.forEach((item) => {
            item._checked = false;
            item.skuInfo = item.skuInfo?.map((sku) => {
              return {
                ...sku,
                _checked: false,
              };
            });
            if (!draft[item.externalItemId]) {
              draft[item.externalItemId] = item;
            }
          });
        }),
      );
      setCount(showItemList.length);
    },
  });

  return [runAsync, loading] as const;
}

export function useDefaultItems() {
  const setMap = useSetAtom(ItemIdMap);
  const { runAsync } = useRequest(
    (items: ItemSelectV2.DefaultItem[]) =>
      getItemsByInternalIds(
        items.map((e) => {
          return {
            itemInternalId: e.internalItemId,
            skus: e.skus,
          };
        }),
      ),
    {
      manual: true,
      onSuccess: (itemList, [items]) => {
        const itemsIdMap = items.reduce((acc, cur) => {
          acc[cur.spuId] = cur;
          return acc;
        }, {} as Record<string, ItemSelectV2.DefaultItem>);
        setMap(
          produce((draft) => {
            itemList.forEach((item) => {
              if (!draft[item.externalItemId]) {
                draft[item.externalItemId] = item;
              }
              const existItem = draft[item.externalItemId];
              existItem._checked = !!itemsIdMap[existItem.externalItemId];
              existItem.skuInfo = existItem.skuInfo?.map((sku) => {
                return {
                  ...sku,
                  _checked: !!itemsIdMap[existItem.externalItemId]?.skus?.some(
                    (e) => e.skuId === sku.skuId,
                  ),
                };
              });
            });
          }),
        );
      },
    },
  );

  return runAsync;
}

export function useQuickSelectItems() {
  const setMap = useSetAtom(ItemIdMap);
  const { runAsync } = useRequest((params) => queryItemListByKeyword(params), {
    manual: true,
    onSuccess: (data, [{ keywordType, keyword }]) => {
      const ids = keyword.split(',');
      const lowerCaseIds = ids.map((e: string) => e.toLowerCase());
      const { itemList } = data;
      setMap(
        produce((draft) => {
          itemList.forEach((item) => {
            if (!draft[item.externalItemId]) {
              draft[item.externalItemId] = item;
            }
            const existItem = draft[item.externalItemId];
            if (keywordType === 4) {
              existItem._checked = true;
              existItem.skuInfo.forEach((sku) => {
                if (sku._checked !== true) {
                  sku._checked = lowerCaseIds.includes(sku.sellerSku.toLowerCase());
                }
              });
              existItem._indeterminate = !existItem.skuInfo.every((sku) => sku._checked);
            } else if (keywordType === 3 || keywordType === 2) {
              existItem._checked = true;
              existItem.skuInfo.forEach((sku) => {
                sku._checked = true;
              });
            }
          });
        }),
      );
    },
  });

  return runAsync;
}
